<template>
    <div class="xeEquipmentControl heightMAX" @contextmenu.prevent="onContextMenu">
        <div class="kbet-custom-reset heightMAX">
            <div
                class="kbet-widget-energy heightMAX"
                :class="{
                    'kbet-widget-energy--control-only': selectedNodeInfo && !isLoading && ui.isMinimalMode,
                    'kbet-widget-energy--flat-status': !isLoading && ui.isMinimalMode,
                    'kbet-widget-energy--group': selectedNodeInfo && !isLoading && ui.isGroupNode,
                    'kbet-widget-energy--power-off': selectedNodeInfo && !isLoading && ui.primaryStatusState === 'off',
                    'kbet-widget-energy--loading': selectedNodeInfo && isLoading,
                }"
                :style="ui.surfaceColorVariables"
            >
                <!-- 최초 데이터 로드 중 스켈레톤 (fetch 전 off 처럼 보이는 깜빡임 방지) -->
                <div v-if="selectedNodeInfo && isLoading" class="equipment-skeleton" aria-hidden="true">
                    <div class="equipment-skeleton__lines">
                        <div class="equipment-skeleton__line skeleton-shimmer"></div>
                        <div class="equipment-skeleton__line equipment-skeleton__line--short skeleton-shimmer"></div>
                    </div>
                </div>
                <div
                    v-if="selectedNodeInfo && !isLoading"
                    class="equipment-widget-content"
                    :class="{
                        'is-off': ui.primaryStatusState === 'off',
                        'equipment-widget-content--group': ui.isGroupNode,
                    }"
                    @click="handleClick"
                >
                    <!-- displayMode === minimal: ON/OFF·상태만 표시 -->
                    <div v-if="ui.isMinimalMode" class="control-only-body control-only-body--flat-status">
                        <div class="control-only-icon-row control-only-icon-row--flat-status">
                            <span class="control-only-icon-stage">
                                <span class="control-light-rays" aria-hidden="true">
                                    <span class="control-light-rays__layer control-light-rays__layer--1"></span>
                                    <span class="control-light-rays__layer control-light-rays__layer--2"></span>
                                    <span class="control-light-rays__layer control-light-rays__layer--3"></span>
                                </span>
                                <i :class="ui.controlEquipmentIconClass" :aria-label="ui.powerStateText" role="img">{{
                                    ui.equipmentIconText
                                }}</i>
                            </span>
                            <div class="control-only-switch-inline" @click.stop>
                                <el-switch
                                    v-model="switchOn"
                                    class="equipment-power-switch"
                                    :disabled="isToggling || isEditMode"
                                    :aria-label="$t('전원')"
                                    :active-color="powerSwitchColors.active"
                                    :inactive-color="powerSwitchColors.inactive"
                                    @change="toggleOnOff"
                                />
                            </div>
                        </div>
                        <div class="control-only-title-row">
                            <div class="control-only-title-lead">
                                <span class="control-node-name">{{ getNodeName() }}</span>
                                <div
                                    v-if="ui.groupOperationRateView"
                                    class="equipment-op-rate-inline equipment-op-rate-inline--control-only-stack"
                                    role="group"
                                    :style="ui.groupOperationRateView.style"
                                    :aria-label="ui.groupOperationRateView.ariaLabel"
                                    v-b-tooltip.hover
                                    :title="ui.groupOperationRateView.tooltip"
                                >
                                    <span
                                        v-if="widget.keepAliveStatus !== false"
                                        class="equipment-op-rate-inline__live-dot"
                                        aria-hidden="true"
                                    ></span>
                                    <span class="equipment-op-rate-inline__label">{{ $t("가동률") }}</span>
                                    <span class="equipment-op-rate-inline__value"
                                        >{{ ui.groupOperationRateView.percentOn }}%</span
                                    >
                                    <span class="equipment-op-rate-inline__track" aria-hidden="true"></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- displayMode === full: 핵심 지표·운전 모드 등 전체 표시 -->
                    <template v-else>
                        <div class="full-mode-body">
                            <div class="metric-panel">
                                <div class="equipment-top-row">
                                    <div class="equipment-top-row-lead">
                                        <span class="equipment-node-name">{{ getNodeName() }}</span>
                                        <div
                                            v-if="ui.groupOperationRateView && !ui.isGroupNode"
                                            class="equipment-op-rate-inline"
                                            role="group"
                                            :style="ui.groupOperationRateView.style"
                                            :aria-label="ui.groupOperationRateView.ariaLabel"
                                            v-b-tooltip.hover
                                            :title="ui.groupOperationRateView.tooltip"
                                        >
                                            <span class="equipment-op-rate-inline__label">{{ $t("가동률") }}</span>
                                            <span class="equipment-op-rate-inline__value"
                                                >{{ ui.groupOperationRateView.percentOn }}%</span
                                            >
                                            <span class="equipment-op-rate-inline__track" aria-hidden="true"></span>
                                        </div>
                                    </div>
                                    <div v-if="ui.powerProp" class="equipment-switch-wrap" @click.stop>
                                        <el-switch
                                            v-model="switchOn"
                                            class="equipment-power-switch"
                                            :disabled="isToggling || isEditMode"
                                            :aria-label="$t('전원')"
                                            :active-color="powerSwitchColors.active"
                                            :inactive-color="powerSwitchColors.inactive"
                                            @change="toggleOnOff"
                                        />
                                    </div>
                                </div>

                                <div
                                    v-if="ui.isGroupNode && ui.groupOperationRateView"
                                    class="equipment-group-overview"
                                    role="group"
                                    :aria-label="ui.groupOperationRateView.ariaLabel"
                                >
                                    <div class="group-op-rate-header">
                                        <span class="group-op-rate-header__label">
                                            <span
                                                v-if="widget.keepAliveStatus !== false"
                                                class="group-op-rate-live-dot"
                                                aria-hidden="true"
                                            ></span>
                                            {{ $t("가동률") }}
                                        </span>
                                        <span
                                            class="equipment-node-type-chip equipment-node-type-chip--group-op-rate"
                                            v-b-tooltip.hover
                                            :title="ui.groupOperationRateView.subline"
                                        >
                                            <i class="fas fa-layer-group" aria-hidden="true"></i>
                                            <span>{{ ui.groupOperationRateView.total }}{{ $t("대") }}</span>
                                        </span>
                                    </div>
                                    <div class="group-op-rate-meter-row" :style="ui.groupOperationRateView.meterStyle">
                                        <div class="group-op-rate-pill-meter" aria-hidden="true">
                                            <span
                                                v-for="slot in ui.groupOperationRateView.meterSlots"
                                                :key="slot.index"
                                                class="group-op-rate-pill-meter__slot"
                                                :class="{ 'group-op-rate-pill-meter__slot--active': slot.isActive }"
                                            ></span>
                                        </div>
                                        <strong class="group-op-rate-meter-row__value">
                                            {{ ui.groupOperationRateView.percentOn }}%
                                        </strong>
                                    </div>
                                    <div class="group-op-rate-legend">
                                        <span
                                            v-for="item in ui.groupOperationRateView.summaryItems"
                                            :key="item.state"
                                            class="group-op-rate-legend__item"
                                        >
                                            <span
                                                class="group-op-rate-legend__dot"
                                                :class="`group-op-rate-legend__dot--${item.state}`"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="group-op-rate-legend__label">{{ item.label }}</span>
                                            <strong class="group-op-rate-legend__value">{{ item.count }}</strong>
                                        </span>
                                    </div>
                                </div>

                                <div v-if="ui.heroMetrics.length && !ui.isGroupNode" class="hero-row">
                                    <div
                                        v-for="metric in ui.heroMetrics"
                                        :key="metric.propCode"
                                        class="hero-stat"
                                        :class="{
                                            'hero-stat--primary': metric.role === 'primary',
                                            'hero-stat--emphasized': metric.role === 'emphasized',
                                        }"
                                    >
                                        <span class="hero-label">{{ metric.label }}</span>
                                        <span class="hero-reading">
                                            <strong class="hero-value">{{ metric.value }}</strong>
                                            <span v-if="metric.unitText" class="hero-unit" :style="metric.unitStyle">
                                                {{ metric.unitText }}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                <div v-if="ui.hasModeMeta && !ui.isGroupNode" class="meta-section">
                                    <div class="meta-split-line"></div>
                                    <div class="mode-meta-row">
                                        <span
                                            v-if="ui.operationModeProp"
                                            class="mode-chip"
                                            :class="
                                                ui.operationModeTone
                                                    ? `mode-chip--${ui.operationModeTone}`
                                                    : 'mode-chip--neutral'
                                            "
                                        >
                                            <span
                                                v-if="switchOn"
                                                class="equipment-op-rate-inline__live-dot"
                                                aria-hidden="true"
                                            ></span>
                                            {{ ui.formattedOperationModeText || emptyDisplayValue }}
                                        </span>

                                        <div v-if="ui.fanMetaProps.length" class="meta-labels-row">
                                            <span
                                                v-for="(meta, idx) in ui.fanMetaProps"
                                                :key="meta.propCode"
                                                class="meta-labeled-unit"
                                            >
                                                <span
                                                    class="meta-labeled-pair"
                                                    v-b-tooltip.hover
                                                    :title="meta.propName"
                                                >
                                                    <span class="meta-labeled-label">{{ metaShortLabel(meta) }}</span>
                                                    <span class="meta-labeled-value">{{
                                                        formatMetaValueBrief(meta)
                                                    }}</span>
                                                </span>
                                                <span
                                                    v-if="idx < ui.fanMetaProps.length - 1"
                                                    class="meta-labeled-dot"
                                                    aria-hidden="true"
                                                    >·</span
                                                >
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- 노드 미설정 빈 상태 -->
                <div v-if="!selectedNodeInfo" class="widget-empty-wrap">
                    <div class="no-node-info-guide">
                        <div class="empty-state-icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="empty-state-message">
                            {{ $t("설정에서 노드를 선택해주세요.") }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- 에러 뱃지: overflow:hidden 밖에 배치해 클리핑 방지 -->
            <transition name="equipment-error-fade">
                <div
                    v-if="selectedNodeInfo && hasEquipError"
                    class="equipment-error-badge"
                    role="alert"
                    v-b-tooltip.hover
                    :title="$t('오류')"
                    :aria-label="$t('오류')"
                >
                    <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                </div>
            </transition>
        </div>

        <!-- 설정 모달 -->
        <b-modal
            ref="modalSetting"
            :title="`${$t('설정')} - ${$t('설비 제어')}`"
            body-class="p-t-0 p-b-0"
            hide-footer
            @hidden="onModalCancel"
        >
            <equipment-control-setting
                :initSetting="getEquipmentControlSetting()"
                @on-modal-cancel="onModalCancel"
                @on-modal-ok="onModalOK"
            />
        </b-modal>

        <!-- 설비 제어 모달 -->
        <b-modal
            v-if="selectedNodeInfo"
            ref="controlModal"
            :title="getControlModalTitle()"
            body-class="p-0"
            hide-footer
            centered
            @hidden="closeControlModal"
        >
            <equipment-control-modal
                :nodeInfo="selectedNodeInfo"
                @load-status="loadNodeStatusData"
                @close="closeControlModal"
            />
        </b-modal>
    </div>
</template>

<script>
import xeBaseWidget from "@src/views/widget/v1/common/xeBaseWidget.js";
import EquipmentControlSetting from "./setting/xeEquipmentControlSetting";
import EquipmentControlModal from "./control/xeEquipmentControlModal";
import { mapState } from "vuex";
import backEndApi from "@api/backEndApi.js";
import { getEquipTypeInfo, getEquipStatus, invalidateEquipStatus } from "@src/views/widget/v1/common/equipMetaCache.js";
import "element-ui/lib/theme-chalk/switch.css";

const EMPTY_DISPLAY_VALUE = "—";
const GROUP_NODE_TYPE = "Group";
const REFRESH_INTERVAL_MS = 10 * 1000;
const KEEP_ALIVE_ACTIVE_MS = 5 * 60 * 1000;
const HTTP_OK = 200;
const META_PROP_LIMIT = 3;
const META_LABEL_MAX_LENGTH = 5;
const META_VALUE_MAX_LENGTH = 8;
const TRUNCATED_META_LABEL_LENGTH = 4;
const TRUNCATED_META_VALUE_LENGTH = 7;
const DEFAULT_EQUIPMENT_ICON = "fas fa-cog";
const KEEP_ALIVE_TIME_PATTERN = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})/;
const JS_MONTH_INDEX_OFFSET = 1;
const GROUP_OPERATION_RATE_SLOT_COUNT = 14;

const POWER_VALUES = Object.freeze({
    ON: 1,
    OFF: 0,
});

const POWER_SWITCH_COLORS = Object.freeze({
    active: "#3b82f6",
    inactive: "#cbd5e1",
});

const CONTROL_COMMAND = Object.freeze({
    mode: "Simple",
    expandSearch: "Y",
    successResult: "Success",
    permissionDenied: "PermissionDenied",
});

const SURFACE_RGB = Object.freeze({
    flatStatus: [70, 84, 102],
    group: [59, 130, 246],
    powerOff: [236, 236, 236],
    cool: [20, 120, 190],
    heat: [205, 90, 58],
    neutral: [22, 132, 124],
});

const HERO_UNIT_FONT_SIZES = Object.freeze([
    { minWidth: 8, fontSize: "20px" },
    { minWidth: 6, fontSize: "16px" },
]);

const DEFAULT_HERO_UNIT_FONT_SIZE = "13px";

const PROP_KEYWORDS = Object.freeze({
    power: ["POWER", "ONOFF"],
    heroPrimary: ["ROOMTEMP"],
    heroSecondary: ["SETTEMP", "SETROOMTEMP"],
    operationMode: ["OPMODE", "MODE"],
    fanMeta: ["FANFLOW", "FANSPEED", "WIND", "FAN"],
    primaryStatus: ["ONOFF", "POWER", "STATUS", "STATE", "RUN"],
});

const MODE_KEYWORDS = Object.freeze({
    cool: ["cool", "냉"],
    heat: ["heat", "난", "warm"],
});

const STATUS_TEXT_PATTERNS = Object.freeze({
    on: /(^|\s)(on|open|run|running|active)(\s|$)|켜짐|운전|가동|활성/,
    off: /(^|\s)(off|close|stop|stopped|inactive)(\s|$)|꺼짐|정지|중지|비활성/,
});

const ON_LABELS = new Set(["ON", "켜짐"]);
const OFF_LABELS = new Set(["OFF", "꺼짐"]);

const EQUIP_PROP_GROUPS = ["ioProps", "calcProps", "userProps"];

/** 위젯에서 참조하는 포인트 필드만 유지 */
function pickPropSnapshot(prop) {
    if (!prop) return null;
    return {
        propCode: prop.propCode,
        propName: prop.propName,
        ptAddr: prop.ptAddr,
        ptIdx: prop.ptIdx,
        ptType: prop.ptType,
        ptVal: prop.ptVal,
        unit: prop.unit,
        unitType: prop.unitType,
    };
}

function pickPropGroupSnapshot(props) {
    return (props || []).map(pickPropSnapshot).filter(Boolean);
}

/** 설비 1대 스냅샷 — io/calc/user props 분리 보관 */
function pickEquipStatusSnapshot(source) {
    if (!source) return null;
    return {
        equipIdx: source.equipIdx,
        equipName: source.equipName,
        equipType: source.equipType,
        ioProps: pickPropGroupSnapshot(source.ioProps),
        calcProps: pickPropGroupSnapshot(source.calcProps),
        userProps: pickPropGroupSnapshot(source.userProps),
    };
}

function normalizeEquipStatusView(source) {
    if (!source) return null;
    if (Array.isArray(source)) {
        return source.map(pickEquipStatusSnapshot).filter(Boolean);
    }
    return pickEquipStatusSnapshot(source);
}

function forEachEquipProp(statusView, callback) {
    if (!statusView || typeof callback !== "function") return;
    const scanEquip = (equip) => {
        EQUIP_PROP_GROUPS.forEach((groupKey) => {
            (equip?.[groupKey] || []).forEach((prop) => callback(prop, equip, groupKey));
        });
    };
    if (Array.isArray(statusView)) {
        statusView.forEach(scanEquip);
    } else {
        scanEquip(statusView);
    }
}

export default {
    name: "xeEquipmentControl",
    extends: xeBaseWidget,
    components: {
        EquipmentControlSetting,
        EquipmentControlModal,
    },
    props: {
        contextMenu: {
            type: Object,
            default: () => ({}),
        },
        width: {
            type: Number,
            default: 1,
        },
        height: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            selectedNodeInfo: null,
            equipTypeInfo: null,
            // 동일 바인딩(sysNodeIdx:equipType)에 대한 중복 로드 방지용 키
            dataKey: null,
            // 최초 데이터 로드 중 스켈레톤 표시용 (fetch 전 off 처럼 보이는 깜빡임 방지)
            isLoading: true,
            refreshTimer: null,
            switchOn: false,
            isToggling: false,
            /** 공유 캐시에서 가져온 설비 상태에서 io/calc/user props 만 추린 뷰 */
            equipStatusView: null,
            emptyDisplayValue: EMPTY_DISPLAY_VALUE,
            powerSwitchColors: POWER_SWITCH_COLORS,
        };
    },
    computed: {
        ...mapState({
            units: (state) => state.units,
            totalReal: (state) => state.totalReal,
            keepAlive: (state) => state.keepAlive,
        }),
        /** 설비 노드 단일 객체의 io·calc·user props (그룹 노드는 사용하지 않음) */
        currentEquipProps() {
            return this.getCurrentIoProps();
        },
        /** 표시·상태 파생값을 한 번에 계산 (ioProps 스캔·상태 판별 중복 제거) */
        ui() {
            return this.buildUiContext();
        },
        hasEquipError() {
            return this.getEquipErrorStatus();
            //에러뱃지 테스트
            // return true;
        },
    },
    watch: {
        equipStatusView: {
            handler() {
                this.syncSwitchOnFromStatus();
            },
            deep: true,
            immediate: true,
        },
        selectedNodeInfo() {
            this.syncSwitchOnFromStatus();
        },
        totalReal: {
            handler(newTotalReal) {
                if (newTotalReal && this.equipStatusView) {
                    this.applyRealtimeFromStore(newTotalReal);
                    this.syncSwitchOnFromStatus();
                }
            },
            deep: true,
        },
        // 바인딩(노드)이 바뀌면 설비 메타/상태를 캐시에서 새로 로드한다.
        "widget.nodeInfo": {
            handler() {
                this.applyData();
            },
            deep: true,
            immediate: true,
        },
        keepAlive: {
            handler() {
                this.updateKeepAliveStatus();
            },
            deep: true,
        },
    },
    created() {
        if (!this.widget.moved) {
            this.widget.moved = false;
        }
        if (!this.widget.objects) {
            this.widget.objects = {
                main: {
                    label: "",
                    addition: {
                        afterAction: "",
                        afterBind: "",
                        isPercentage: true,
                        fixed: 0,
                    },
                    tail: "",
                    point: "",
                    value: 0,
                    self: { type: 0, sTi: "", eTi: "" },
                },
                bottom: {
                    label: "",
                    addition: {
                        afterAction: "",
                        afterBind: "",
                        isPercentage: false,
                        fixed: 0,
                    },
                    tail: "",
                    point: "",
                    value: 0,
                    self: { type: 0, sTi: "", eTi: "" },
                },
                series: { backgroundColor: "" },
            };
        }
        if (!this.widget.title) {
            this.widget.title = "";
        }
        if (!this.widget.keepAliveStatus) {
            this.widget.keepAliveStatus = true;
        }
        if (!this.widget.displayMode) {
            this.widget.displayMode = "full";
        }
    },
    mounted() {
        // 그리드 아이템이 높이를 위젯 루트에 위임하므로 부모 높이를 한 번 보정한다.
        if (this.$el?.parentNode) {
            this.$el.parentNode.style.height = "100%";
        }

        this.updateKeepAliveStatus();

        this.refreshTimer = setInterval(() => {
            try {
                this.applyData();
            } catch (error) {
                console.error("주기적 데이터 갱신 중 오류:", error);
            }
        }, REFRESH_INTERVAL_MS);
    },
    beforeDestroy() {
        clearInterval(this.refreshTimer);
    },
    methods: {
        getEquipErrorStatus() {
            const ioProps = this.currentEquipProps?.ioProps ?? [];
            const errorProp = ioProps.find((prop) => prop.propCode === "ERROR_CODE");
            if (!errorProp || !errorProp.ptAddr) return false;
            const errorValue = this.totalReal?.[errorProp.ptAddr]?.ptVal;
            return Boolean(errorValue && errorValue !== 0);
        },

        createSurfaceColorVars([r, g, b]) {
            return {
                "--surface-r": r,
                "--surface-g": g,
                "--surface-b": b,
            };
        },

        hasPropCodeKeyword(prop, keywords) {
            const code = (prop?.propCode || "").toUpperCase();
            return keywords.some((keyword) => code.includes(keyword));
        },

        getCurrentIoProps() {
            const status = this.equipStatusView;
            if (!status || Array.isArray(status)) {
                return { ioProps: [], calcProps: [], userProps: [] };
            }
            return {
                ioProps: status.ioProps || [],
                calcProps: status.calcProps || [],
                userProps: status.userProps || [],
            };
        },

        getGroupPowerProp() {
            const list = this.equipStatusView;
            if (!Array.isArray(list) || !list.length) return null;
            const firstProps = list[0]?.ioProps;
            if (!firstProps?.length) return null;
            return firstProps.find((prop) => this.hasPropCodeKeyword(prop, PROP_KEYWORDS.power)) || null;
        },

        findIoPropIn(ioProps, keywords) {
            return ioProps.find((prop) => this.hasPropCodeKeyword(prop, keywords)) || null;
        },

        isGroupNode() {
            return this.selectedNodeInfo?.nodeType === GROUP_NODE_TYPE;
        },

        syncSwitchOnFromStatus() {
            if (this.isToggling) return;
            this.switchOn = this.ui.isOn;
        },

        getEquipmentControlSetting() {
            return JSON.parse(JSON.stringify(this.widget));
        },

        getControlModalTitle() {
            const nodeText = this.selectedNodeInfo?.text ? ` (${this.selectedNodeInfo.text})` : "";
            return this.$t(`설비제어${nodeText}`);
        },

        getHeroUnitFontSize() {
            const matched = HERO_UNIT_FONT_SIZES.find(({ minWidth }) => this.width >= minWidth);
            return matched?.fontSize || DEFAULT_HERO_UNIT_FONT_SIZE;
        },

        formatHeroMetricValue(prop, format) {
            if (!prop) return format === "decimal" ? null : EMPTY_DISPLAY_VALUE;

            const propInfo = this.equipTypeInfo?.ioProps?.find((item) => item.propCode === prop.propCode);
            if (propInfo?.encodeType === "Enum") {
                const formatted = this.formatValue(prop.ptVal, propInfo);
                if (formatted === "N/A") return format === "decimal" ? null : EMPTY_DISPLAY_VALUE;
                return formatted;
            }

            const val = prop.ptVal;
            if (val === null || val === undefined) return format === "decimal" ? null : EMPTY_DISPLAY_VALUE;
            const num = parseFloat(val);
            if (Number.isNaN(num)) return String(val);
            return format === "decimal" ? num.toFixed(1) : Math.round(num).toString();
        },

        buildHeroMetric(prop, { format, role }) {
            if (!prop) return null;

            const propInfo = this.equipTypeInfo?.ioProps?.find((item) => item.propCode === prop.propCode);
            const value = this.formatHeroMetricValue(prop, format);
            if (format === "decimal" && value === null) return null;

            const isEnum = propInfo?.encodeType === "Enum";
            const unitText = isEnum ? "" : this.getUnitText(prop.unit);

            return {
                propCode: prop.propCode,
                label: prop.propName || prop.propCode,
                value: value ?? EMPTY_DISPLAY_VALUE,
                unitText,
                role,
                unitStyle: role === "emphasized" && unitText ? { fontSize: this.getHeroUnitFontSize() } : undefined,
            };
        },

        buildHeroMetrics(heroPrimaryProp, heroSecondaryProp) {
            const metrics = [];
            const primary = this.buildHeroMetric(heroPrimaryProp, { format: "decimal", role: "primary" });
            if (primary) metrics.push(primary);
            if (heroSecondaryProp) {
                const secondary = this.buildHeroMetric(heroSecondaryProp, {
                    format: "round",
                    role: "emphasized",
                });
                if (secondary) metrics.push(secondary);
            }
            return metrics;
        },

        resolveOperationModeTone(operationModeProp) {
            if (!operationModeProp) return null;
            const formatted = String(this.formatPropValue(operationModeProp) || "").toLowerCase();
            if (MODE_KEYWORDS.cool.some((keyword) => formatted.includes(keyword))) return "cool";
            if (MODE_KEYWORDS.heat.some((keyword) => formatted.includes(keyword))) return "heat";
            return null;
        },

        resolvePrimaryStatusState({ isGroupNode, groupCounts, primaryStatusProp }) {
            if (isGroupNode && groupCounts) {
                if (groupCounts.onCount > 0) return "on";
                if (groupCounts.offCount === groupCounts.total) return "off";
                return "neutral";
            }

            if (!primaryStatusProp) return "neutral";

            const raw = primaryStatusProp.ptVal;
            const rawNumber = Number(raw);
            const statusLabel = this.formatPropValue(primaryStatusProp);
            const normalizedText = `${raw ?? ""} ${statusLabel}`.toLowerCase();

            if (raw === true || rawNumber === POWER_VALUES.ON) return "on";
            if (raw === false || rawNumber === POWER_VALUES.OFF) return "off";
            if (STATUS_TEXT_PATTERNS.on.test(normalizedText)) return "on";
            if (STATUS_TEXT_PATTERNS.off.test(normalizedText)) return "off";
            return "neutral";
        },

        resolvePowerStateText(primaryStatusState, primaryStatusProp) {
            if (primaryStatusState === "on") return this.$t("켜짐");
            if (primaryStatusState === "off") return this.$t("꺼짐");
            const label = primaryStatusProp ? this.formatPropValue(primaryStatusProp) : "";
            return label && label !== "N/A" ? label : this.$t("상태 확인");
        },

        resolveEquipmentIconSource() {
            const bgIcon = (this.widget.bgIcon || "").trim();
            if (bgIcon) return bgIcon;
            const equipTypeIcon = (this.equipTypeInfo?.icon || "").trim();
            if (equipTypeIcon) return equipTypeIcon;
            return DEFAULT_EQUIPMENT_ICON;
        },

        resolveEquipmentIconGlyph(iconSource) {
            const raw = (iconSource || "").trim();
            if (!raw) return { classes: [DEFAULT_EQUIPMENT_ICON], text: "" };
            if (raw.includes("material")) {
                return {
                    classes: ["material-icons"],
                    text: raw.replace(/^material\s+/, "").trim(),
                };
            }
            return { classes: [raw], text: "" };
        },

        resolveControlEquipmentIconStateClass({
            powerProp,
            isGroupNode,
            primaryStatusState,
            isMinimalMode,
            hasStatusPtVal,
        }) {
            if (isMinimalMode && hasStatusPtVal) {
                return "control-equipment-icon--bulb";
            }
            if (powerProp) {
                if (isGroupNode) return "control-equipment-icon--group";
                return this.switchOn ? "control-equipment-icon--on" : "control-equipment-icon--off";
            }
            if (primaryStatusState === "on") return "control-equipment-icon--on";
            if (primaryStatusState === "off") return "control-equipment-icon--off";
            return "control-equipment-icon--neutral";
        },

        hasPropPtVal(prop) {
            if (!prop) return false;
            const val = prop.ptVal;
            return val !== null && val !== undefined && val !== "";
        },

        hasGroupEquipStatusPtVal() {
            const list = this.equipStatusView;
            if (!Array.isArray(list) || !list.length) return false;

            const powerPropCode = this.getGroupPowerProp()?.propCode;
            if (!powerPropCode) return false;

            return list.some((equip) => {
                const prop = equip.ioProps?.find((item) => item.propCode === powerPropCode);
                return this.hasPropPtVal(prop);
            });
        },

        resolveHasStatusPtVal({ isGroupNode, primaryStatusProp }) {
            if (isGroupNode) return this.hasGroupEquipStatusPtVal();
            return this.hasPropPtVal(primaryStatusProp);
        },

        buildControlEquipmentIconClass({ powerProp, isGroupNode, primaryStatusState, isMinimalMode, hasStatusPtVal }) {
            const glyph = this.resolveEquipmentIconGlyph(this.resolveEquipmentIconSource());
            const stateClass = this.resolveControlEquipmentIconStateClass({
                powerProp,
                isGroupNode,
                primaryStatusState,
                isMinimalMode,
                hasStatusPtVal,
            });
            return {
                controlEquipmentIconClass: [...glyph.classes, "control-equipment-icon", stateClass],
                equipmentIconText: glyph.text,
            };
        },

        resolveSurfaceColorVariables({ isMinimalMode, isGroupNode, primaryStatusState, operationModeTone }) {
            if (primaryStatusState === "off") return this.createSurfaceColorVars(SURFACE_RGB.powerOff);
            if (isGroupNode) return this.createSurfaceColorVars(SURFACE_RGB.group);
            if (isMinimalMode) return this.createSurfaceColorVars(SURFACE_RGB.flatStatus);
            if (operationModeTone === "cool") return this.createSurfaceColorVars(SURFACE_RGB.cool);
            if (operationModeTone === "heat") return this.createSurfaceColorVars(SURFACE_RGB.heat);
            return this.createSurfaceColorVars(SURFACE_RGB.neutral);
        },

        buildGroupMeterSlots(percentOn, slotCount = GROUP_OPERATION_RATE_SLOT_COUNT) {
            const activeCount = Math.round((percentOn / 100) * slotCount);
            return Array.from({ length: slotCount }, (_, index) => ({
                index,
                isActive: index < activeCount,
            }));
        },

        buildGroupOperationRateView(groupCounts) {
            const { onCount, offCount, otherCount, total } = groupCounts;
            if (!total) return null;

            const percentOn = Math.round((onCount / total) * 100);
            const summaryItems = [
                { state: "on", label: this.$t("켜짐"), count: onCount },
                { state: "off", label: this.$t("꺼짐"), count: offCount },
            ];
            if (otherCount > 0) {
                summaryItems.push({ state: "other", label: this.$t("기타"), count: otherCount });
            }
            const meterSlots = this.buildGroupMeterSlots(percentOn);
            const captionParts = [`${this.$t("켜짐")} ${onCount}`, `${this.$t("꺼짐")} ${offCount}`];
            if (otherCount > 0) captionParts.push(`${this.$t("기타")} ${otherCount}`);
            captionParts.push(`${this.$t("전체")} ${total}`);
            const caption = captionParts.join(" · ");
            const tooltip = [`${this.$t("가동률")} ${percentOn}%`, caption].join("\n");
            const ariaLabel = `${this.$t("가동률")} ${percentOn}퍼센트, ${caption}`;

            return {
                onCount,
                offCount,
                otherCount,
                total,
                percentOn,
                meterSlots,
                summaryItems,
                style: {
                    "--op-rate": `${percentOn}%`,
                    "--op-rate-color": "var(--equipment-group-accent, #3b82f6)",
                    "--op-rate-shadow-color": "rgba(59, 130, 246, 0.18)",
                },
                meterStyle: {
                    "--group-op-rate-slots": GROUP_OPERATION_RATE_SLOT_COUNT,
                },
                caption,
                subline: caption,
                tooltip,
                ariaLabel,
            };
        },

        buildUiContext() {
            const isGroupNode = this.isGroupNode();
            const groupCounts = isGroupNode ? this.getGroupOnOffCounts() : null;

            let powerProp;
            let heroMetrics;
            let operationModeProp;
            let fanMetaProps;
            let primaryStatusProp;

            if (isGroupNode) {
                // 그룹 노드: 서버 그룹 전용 표시 지표 없음 — 가동률·전원 집계·제어만 (하위 설비 prop 재집계 미표시)
                powerProp = this.getGroupPowerProp();
                heroMetrics = [];
                operationModeProp = null;
                fanMetaProps = [];
                primaryStatusProp = powerProp;
            } else {
                const ioProps = this.getCurrentIoProps().ioProps;
                powerProp = this.findIoPropIn(ioProps, PROP_KEYWORDS.power);
                const heroSecondaryProp = this.findIoPropIn(ioProps, PROP_KEYWORDS.heroSecondary);
                const heroPrimaryProp = this.findIoPropIn(ioProps, PROP_KEYWORDS.heroPrimary);
                operationModeProp = this.findIoPropIn(ioProps, PROP_KEYWORDS.operationMode);
                fanMetaProps = ioProps
                    .filter((prop) => this.hasPropCodeKeyword(prop, PROP_KEYWORDS.fanMeta))
                    .slice(0, META_PROP_LIMIT);
                primaryStatusProp =
                    powerProp || this.findIoPropIn(ioProps, PROP_KEYWORDS.primaryStatus) || ioProps[0] || null;
                heroMetrics = this.buildHeroMetrics(heroPrimaryProp, heroSecondaryProp);
            }

            const primaryStatusState = this.resolvePrimaryStatusState({
                isGroupNode,
                groupCounts,
                primaryStatusProp,
            });

            const isMinimalMode = this.widget.displayMode === "minimal";
            const hasStatusPtVal = this.resolveHasStatusPtVal({ isGroupNode, primaryStatusProp });
            const operationModeTone = this.resolveOperationModeTone(operationModeProp);

            let isOn = false;
            if (powerProp) {
                if (isGroupNode && groupCounts) {
                    isOn = groupCounts.onCount > 0;
                } else {
                    isOn = this.toPowerState(powerProp.ptVal) === "on";
                }
            }

            return {
                powerProp,
                operationModeProp,
                fanMetaProps,
                primaryStatusState,
                powerStateText: this.resolvePowerStateText(primaryStatusState, primaryStatusProp),
                ...this.buildControlEquipmentIconClass({
                    powerProp,
                    isGroupNode,
                    primaryStatusState,
                    isMinimalMode,
                    hasStatusPtVal,
                }),
                heroMetrics,
                formattedOperationModeText: operationModeProp ? this.formatPropValue(operationModeProp) : "",
                hasModeMeta: Boolean(operationModeProp || fanMetaProps.length),
                operationModeTone,
                surfaceColorVariables: this.resolveSurfaceColorVariables({
                    isMinimalMode,
                    isGroupNode,
                    primaryStatusState,
                    operationModeTone,
                }),
                isMinimalMode,
                isGroupNode,
                groupOperationRateView: groupCounts ? this.buildGroupOperationRateView(groupCounts) : null,
                isOn,
            };
        },

        getRealtimePropValue(prop) {
            return prop?.ptVal;
        },

        rebuildEquipStatusView(source) {
            if (this.isEmpty(source)) {
                this.equipStatusView = null;
                return;
            }

            this.equipStatusView = normalizeEquipStatusView(source);
            this.applyRealtimeFromStore(this.totalReal);
        },

        /** totalReal 값을 equipStatusView 의 io/calc/user props.ptVal 에 직접 반영 */
        applyRealtimeFromStore(totalReal) {
            if (!totalReal || typeof totalReal !== "object" || !this.equipStatusView) return;

            forEachEquipProp(this.equipStatusView, (prop) => {
                if (!prop?.ptAddr) return;
                const realtimePoint = totalReal[prop.ptAddr];
                if (realtimePoint) {
                    this.$set(prop, "ptVal", realtimePoint.ptVal);
                }
            });
        },

        toPowerState(value) {
            if (value === true) return "on";
            if (value === false) return "off";

            const numericValue = Number(value);
            if (value !== "" && value !== null && value !== undefined && !Number.isNaN(numericValue)) {
                if (numericValue === POWER_VALUES.ON) return "on";
                if (numericValue === POWER_VALUES.OFF) return "off";
            }

            const label = String(value ?? "").trim();
            const upperLabel = label.toUpperCase();
            if (ON_LABELS.has(upperLabel)) return "on";
            if (OFF_LABELS.has(upperLabel)) return "off";
            if (upperLabel.includes("ON") && !upperLabel.includes("OFF")) return "on";
            if (upperLabel.includes("OFF") || label.includes("꺼")) return "off";
            return "neutral";
        },

        /**
         * 그룹(currentEquipStatus: 설비별 배열)에서 첫 번째 ONOFF/POWER 포인트 기준으로 켜짐/꺼짐 집계
         * (가동률·그룹 스위치/primaryStatusState: 하나라도 on이면 on, 전부 off면 off)
         */
        getGroupOnOffCounts() {
            if (this.selectedNodeInfo?.nodeType !== GROUP_NODE_TYPE) return null;
            const list = this.equipStatusView;
            if (!Array.isArray(list) || list.length === 0) return null;

            const firstProps = list[0]?.ioProps;
            if (!firstProps?.length) return null;

            const powerProp = firstProps.find((prop) => this.hasPropCodeKeyword(prop, PROP_KEYWORDS.power));
            if (!powerProp) return null;

            const powerPropCode = powerProp.propCode;
            let onCount = 0;
            let offCount = 0;
            let otherCount = 0;

            for (const equip of list) {
                const prop = equip.ioProps?.find((x) => x.propCode === powerPropCode);
                if (!prop) {
                    otherCount++;
                    continue;
                }

                const raw = this.getRealtimePropValue(prop);
                let powerState = this.toPowerState(raw);

                if (powerState === "neutral") {
                    const displayValue = this.getDisplayValue({ value: raw, propCode: powerPropCode });
                    powerState = this.toPowerState(displayValue);
                }

                if (powerState === "on") {
                    onCount++;
                    continue;
                }
                if (powerState === "off") {
                    offCount++;
                    continue;
                }

                otherCount++;
            }

            const total = onCount + offCount + otherCount;
            if (total === 0) return null;

            return { onCount, offCount, otherCount, total };
        },

        getOperationRateTone(percentOn) {
            if (percentOn >= 70) {
                return {
                    color: "#16a34a",
                    softColor: "rgba(22, 163, 74, 0.14)",
                    shadowColor: "rgba(22, 163, 74, 0.24)",
                };
            }
            if (percentOn >= 35) {
                return {
                    color: "#f59e0b",
                    softColor: "rgba(245, 158, 11, 0.16)",
                    shadowColor: "rgba(245, 158, 11, 0.24)",
                };
            }
            return {
                color: "#ef4444",
                softColor: "rgba(239, 68, 68, 0.14)",
                shadowColor: "rgba(239, 68, 68, 0.22)",
            };
        },

        applyData() {
            if (this.isEmpty(this.widget.nodeInfo)) return;
            this.selectedNodeInfo = this.widget.nodeInfo;
            this.loadWidgetData();
        },

        /**
         * 위젯 데이터(equipTypeInfo·설비 상태 구조)를 공유 캐시에서 로드한다.
         *  - 저장본(config)에는 바인딩 키만 있으므로 런타임 데이터는 여기서 가져온다.
         *  - 동일 바인딩이면(dataKey) 재호출하지 않는다. 실시간 값은 totalReal watch 가 덮는다.
         */
        async loadWidgetData(force = false) {
            const node = this.selectedNodeInfo;
            if (!node?.equipType) {
                this.isLoading = false;
                return;
            }

            const key = `${node.sysNodeIdx}:${node.equipType}`;
            if (!force && this.dataKey === key) return;
            this.dataKey = key;

            // 최초 로드(아직 표시할 데이터가 없음)에만 스켈레톤 노출.
            // 이미 데이터가 있는 갱신(force)에서는 깜빡임 없이 그대로 둔다.
            if (!this.equipStatusView) this.isLoading = true;

            try {
                const [typeInfo, status] = await Promise.all([
                    getEquipTypeInfo(node.equipType),
                    getEquipStatus(node.sysNodeIdx, node.equipType, node.nodeType),
                ]);
                this.equipTypeInfo = typeInfo || null;
                this.rebuildEquipStatusView(status);
            } catch (error) {
                this.dataKey = null; // 실패 시 다음 틱에 재시도 허용
                console.error("설비 위젯 데이터 로드 실패:", error);
            } finally {
                this.isLoading = false;
            }
        },

        getUnitText(unit) {
            if (!unit) return "";
            const unitList = Array.isArray(this.units) ? this.units : [];
            return unitList.find((code) => code.value === unit)?.text || unit;
        },

        getDisplayValue(item) {
            if (!item || item.value === undefined || item.value === null) return "N/A";
            return this.formatDisplayValue(item);
        },

        formatDisplayValue(item) {
            if (!this.equipTypeInfo?.ioProps) {
                if (item.unit) {
                    const unitText = this.getUnitText(item.unit);
                    return `${item.value} ${unitText}`;
                }
                return item.value;
            }

            const propInfo = this.equipTypeInfo.ioProps.find((prop) => prop.propCode === item.propCode);
            if (!propInfo) {
                const propInfoByName = this.equipTypeInfo.ioProps.find((prop) => prop.propName === item.label);
                if (!propInfoByName) {
                    if (item.unit) {
                        const unitText = this.getUnitText(item.unit);
                        return `${item.value} ${unitText}`;
                    }
                    return item.value;
                }
                return this.formatValue(item.value, propInfoByName);
            }

            return this.formatValue(item.value, propInfo);
        },

        formatValue(value, propInfo = {}) {
            if (value === undefined || value === null) return "N/A";

            if (propInfo.encodeType === "Enum" && propInfo.encodeDesc) {
                try {
                    const enumValues = JSON.parse(propInfo.encodeDesc);
                    const numericValue = parseFloat(value);
                    if (!isNaN(numericValue) && enumValues[numericValue] !== undefined) {
                        return enumValues[numericValue];
                    }
                } catch (error) {
                    console.error("Error parsing enum values:", error);
                }
            }

            if ((propInfo.encodeType === "Range" || propInfo.encodeType === "Number") && propInfo.unit) {
                return `${value} ${this.getUnitText(propInfo.unit)}`;
            }

            if (propInfo.unit) {
                return `${value} ${this.getUnitText(propInfo.unit)}`;
            }

            return value;
        },

        formatPropValue(prop) {
            if (!prop) return "N/A";
            const val = prop.ptVal;
            if (val === null || val === undefined) return "N/A";
            const propInfo = this.equipTypeInfo?.ioProps?.find((p) => p.propCode === prop.propCode);
            if (propInfo) return this.formatValue(val, propInfo);
            return String(val);
        },

        metaShortLabel(prop) {
            if (!prop) return "";
            const name = (prop.propName || "").trim();
            if (name) {
                return name.length <= META_LABEL_MAX_LENGTH ? name : `${name.slice(0, TRUNCATED_META_LABEL_LENGTH)}…`;
            }
            return prop.propCode || EMPTY_DISPLAY_VALUE;
        },

        formatMetaValueBrief(prop) {
            let s = String(this.formatPropValue(prop) || EMPTY_DISPLAY_VALUE)
                .trim()
                .replace(/\s+/g, " ");
            s = s.replace(/\([^)]*\)/g, "").trim();
            if (s.length > META_VALUE_MAX_LENGTH) return `${s.slice(0, TRUNCATED_META_VALUE_LENGTH)}…`;
            return s;
        },

        async toggleOnOff(newVal) {
            if (!this.selectedNodeInfo || !this.ui.powerProp || this.isToggling) {
                this.switchOn = !newVal;
                return;
            }

            this.isToggling = true;

            try {
                const ctrlCmd = {
                    sysNodeIdx: this.selectedNodeInfo.sysNodeIdx,
                    equipType: this.selectedNodeInfo.equipType,
                    ctrlMode: CONTROL_COMMAND.mode,
                    expandSearch: CONTROL_COMMAND.expandSearch,
                    propList: [
                        {
                            propCode: this.ui.powerProp.propCode,
                            ptVal: newVal ? POWER_VALUES.ON : POWER_VALUES.OFF,
                        },
                    ],
                };

                const sendGroupCtrlCmd_response = await backEndApi.sysNode.sendGroupCtrlCmd(ctrlCmd);
                const sendGroupCtrlCmd_data = sendGroupCtrlCmd_response?.data;
                const isControlSuccess =
                    sendGroupCtrlCmd_response?.status === HTTP_OK &&
                    Array.isArray(sendGroupCtrlCmd_data) &&
                    sendGroupCtrlCmd_data[0]?.result === CONTROL_COMMAND.successResult;

                if (!isControlSuccess) {
                    this.switchOn = !newVal;
                    const errorCode = Array.isArray(sendGroupCtrlCmd_data)
                        ? sendGroupCtrlCmd_data[0]?.errcode
                        : sendGroupCtrlCmd_data?.errcode;
                    if (errorCode === CONTROL_COMMAND.permissionDenied) {
                        this.alertWarning(this.$t("제어 권한이 없습니다."));
                    } else {
                        this.alertWarning(this.$t("제어에 실패했습니다."));
                    }
                }
            } catch (error) {
                console.error("ON/OFF toggle error:", error);
                this.switchOn = !newVal;
            } finally {
                this.isToggling = false;
            }
        },

        handleDropPoint(label, arg) {
            this.widget.title = arg.item.title;
            const droppedPoint = this.createDroppedPointObject(arg.item.ptAddr);
            if (label === "main") {
                this.widget.objects.main = droppedPoint;
            } else {
                this.widget.objects.bottom = droppedPoint;
            }
        },

        createDroppedPointObject(point) {
            return {
                label: "",
                addition: { afterAction: "", afterBind: "Last", isPercentage: false },
                tail: "",
                point,
                value: 0,
            };
        },

        getNodeName() {
            return this.widget.title || this.selectedNodeInfo?.text || this.$t("설비 제어");
        },

        updateKeepAliveStatus() {
            const keepAliveData = this.keepAlive;
            if (!this.widget.pointInfo || !Array.isArray(keepAliveData)) {
                this.$set(this.widget, "keepAliveStatus", true);
                return;
            }

            const { serverIdx, channelIdx } = this.widget.pointInfo;

            const matchingKeepAlive = keepAliveData.find(
                (item) => item.serverIdx === serverIdx && item.channelIdx === channelIdx,
            );

            if (!matchingKeepAlive) {
                this.$set(this.widget, "keepAliveStatus", true);
                return;
            }

            const keepAliveTime = this.parseKeepAliveTime(matchingKeepAlive.keepAlive);
            const isActive = Date.now() - keepAliveTime.getTime() < KEEP_ALIVE_ACTIVE_MS;
            this.$set(this.widget, "keepAliveStatus", isActive);
        },

        parseKeepAliveTime(keepAliveString) {
            const match = String(keepAliveString || "").match(KEEP_ALIVE_TIME_PATTERN);
            if (!match) return new Date(0);
            const [, year, month, day, hour, minute] = match.map(Number);
            return new Date(year, month - JS_MONTH_INDEX_OFFSET, day, hour, minute);
        },

        handleClick() {
            if (this.isEditMode) return;
            this.openControlModal();
        },

        openControlModal() {
            if (!this.selectedNodeInfo) return;
            if (this.$refs.controlModal) {
                this.$refs.controlModal.show();
            }
        },

        closeControlModal() {
            if (this.$refs.controlModal) {
                this.$refs.controlModal.hide();
            }
        },

        onModalCancel() {
            if (this.$refs.modalSetting) {
                this.$refs.modalSetting.hide();
            }
        },

        onModalOK(setting) {
            const updatedWidget = Object.assign({}, this.widget, setting);
            this.selectedNodeInfo = setting.nodeInfo;

            if (setting.title) {
                updatedWidget.title = setting.title;
            }

            if (this.$refs.modalSetting) {
                this.$refs.modalSetting.hide();
            }
            this.$emit("on-change-widget", updatedWidget);

            this.$nextTick(() => {
                this.syncDashboardGridConstraints();
            });

            // 바인딩이 바뀌었으니 새 노드 기준으로 메타/상태를 다시 로드
            this.dataKey = null;
            this.loadWidgetData();
        },

        loadNodeStatusData() {
            // 제어 직후 등 강제 갱신: 상태 캐시를 무효화하고 다시 로드
            const node = this.selectedNodeInfo;
            if (node?.sysNodeIdx && node?.equipType) {
                invalidateEquipStatus(node.sysNodeIdx, node.equipType);
            }
            this.loadWidgetData(true);
        },
    },
};
</script>

<style scoped>
/* ── 최초 로드 스켈레톤 ─────────────────────────────────── */
.kbet-widget-energy--loading {
    background: #f4f6f8 !important;
}
/* 로딩 중에는 테마색 글로우(::before)·광택(::after) 레이어를 숨겨 중립 배경 유지 */
.kbet-widget-energy--loading::before,
.kbet-widget-energy--loading::after {
    display: none;
}
.equipment-skeleton {
    --sk-base: #e4e7ec;
    --sk-hi: #f3f5f8;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 18px 20px;
    animation: equipment-skeleton-fade 0.4s ease both;
}
.equipment-skeleton__lines {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}
.equipment-skeleton__line {
    height: 13px;
    width: 72%;
    border-radius: 7px;
}
.equipment-skeleton__line--short {
    width: 42%;
    height: 11px;
    opacity: 0.7;
}
/* 은은하게 흐르는 하이라이트 + 전체 소프트 펄스 */
.skeleton-shimmer {
    position: relative;
    overflow: hidden;
    background: var(--sk-base);
    animation: equipment-skeleton-pulse 2.1s ease-in-out infinite;
}
.skeleton-shimmer::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0) 20%,
        var(--sk-hi) 50%,
        rgba(255, 255, 255, 0) 80%,
        transparent 100%
    );
    animation: equipment-skeleton-sweep 1.6s ease-in-out infinite;
}
@keyframes equipment-skeleton-sweep {
    0% {
        transform: translateX(-100%);
    }
    60%,
    100% {
        transform: translateX(100%);
    }
}
@keyframes equipment-skeleton-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.82;
    }
}
@keyframes equipment-skeleton-fade {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
@media (prefers-reduced-motion: reduce) {
    .skeleton-shimmer,
    .skeleton-shimmer::after,
    .equipment-skeleton {
        animation: none;
    }
}

/* ── 리뉴얼 디자인: #D9E0E7 대시보드 배경 대응 ───────────── */
.kbet-widget-energy {
    --equipment-text-primary: #17202d;
    --equipment-text-strong: #243547;
    --equipment-text-muted: #718093;
    --equipment-text-soft: #8a98aa;
    --equipment-chip-text: #475569;
    --equipment-control-blue: #3b82f6;
    --equipment-control-gray: #94a3b8;
    --equipment-control-neutral: #64748b;
    --equipment-group-accent: #3b82f6;
    --equipment-bulb-yellow: #ffd166;
    --equipment-live-green: #16a34a;
    --equipment-off-red: #ef4444;
    --equipment-warning-amber: #f59e0b;
    --equipment-pill-radius: 999px;
    --equipment-chip-bg: rgba(241, 245, 249, 0.72);
    --equipment-chip-border: rgba(148, 163, 184, 0.14);

    position: relative;
    overflow: hidden; /* 광택 레이어를 둥근 위젯 표면 안에 가두기 위한 클리핑 */
    display: flex;
    flex-direction: column;
    border-radius: 20px;

    container-type: inline-size;
    /* 테마색: 우상단 코어 → 짧게 소멸; ::before에서 흰색보다 위에 두어 글로우가 보이게 함 */
    background:
        radial-gradient(
            ellipse 125% 82% at 104% -10%,
            rgba(var(--surface-r, 22), var(--surface-g, 132), var(--surface-b, 124), 0.78) 0%,
            rgba(var(--surface-r, 22), var(--surface-g, 132), var(--surface-b, 124), 0.32) 18%,
            rgba(var(--surface-r, 22), var(--surface-g, 132), var(--surface-b, 124), 0.08) 38%,
            transparent 56%
        ),
        linear-gradient(
            145deg,
            rgba(255, 255, 255, 0.96) 0%,
            rgba(250, 252, 255, 0.92) 50%,
            rgba(239, 245, 250, 0.94) 100%
        ),
        #f8fbfe;
}

.kbet-widget-energy::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    /* 맨 위 레이어 = 테마(글로우), 그 아래만 은은한 하이라이트 — 흰색이 테마를 덮지 않게 */
    background:
        radial-gradient(
            ellipse 115% 123% at 100% 0%,
            rgba(var(--surface-r, 22), var(--surface-g, 132), var(--surface-b, 124), 0.52) 0%,
            rgba(var(--surface-r, 22), var(--surface-g, 132), var(--surface-b, 124), 0.22) 34%,
            transparent 62%
        ),
        radial-gradient(
            ellipse 98% 75% at 100% -4%,
            rgba(255, 255, 255, 0.22) 0%,
            rgba(255, 255, 255, 0.06) 28%,
            transparent 50%
        );
    opacity: 1;
    transition:
        background 0.45s ease,
        opacity 0.35s ease;
}

.kbet-widget-energy::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    pointer-events: none;
    /* 코너에 또 흰색을 쌓지 않음 — 상단 전체에만 아주 약한 광택 */
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, transparent 36%);
    opacity: 0.55;
}

.kbet-widget-energy.kbet-widget-energy--flat-status {
    background:
        radial-gradient(
            ellipse 125% 82% at 104% -10%,
            rgba(70, 84, 102, 0.52) 0%,
            rgba(70, 84, 102, 0.22) 18%,
            rgba(70, 84, 102, 0.07) 38%,
            transparent 56%
        ),
        linear-gradient(
            145deg,
            rgba(255, 255, 255, 0.96) 0%,
            rgba(249, 252, 255, 0.92) 50%,
            rgba(239, 245, 250, 0.94) 100%
        ),
        #f7f9fc;
    box-shadow:
        8px -10px 34px -4px rgba(70, 84, 102, 0.22),
        inset -32px 32px 52px -18px rgba(70, 84, 102, 0.14),
        0 0 0 1px rgba(70, 84, 102, 0.12),
        0 12px 24px rgba(72, 86, 108, 0.14),
        0 2px 8px rgba(70, 84, 102, 0.08),
        inset 0 0 0 1px rgba(78, 92, 110, 0.14),
        inset 0 1px 0 rgba(255, 255, 255, 0.96),
        inset 0 -1px 0 rgba(52, 64, 78, 0.1);
}

.kbet-widget-energy.kbet-widget-energy--flat-status::after {
    opacity: 0.42;
}

/* 그룹 노드: 그룹 surface(#3b82f6) — 상단 그라데이션만 중앙에서 아래로 */
.kbet-widget-energy.kbet-widget-energy--group {
    background:
        radial-gradient(
            ellipse 125% 82% at 50% -10%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.78) 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.32) 18%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.08) 38%,
            transparent 56%
        ),
        linear-gradient(
            145deg,
            rgba(255, 255, 255, 0.96) 0%,
            rgba(250, 252, 255, 0.92) 50%,
            rgba(239, 246, 255, 0.94) 100%
        ),
        #f8fbfe;
}

.kbet-widget-energy.kbet-widget-energy--group::before {
    background:
        radial-gradient(
            ellipse 130% 100% at 50% 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.52) 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.22) 34%,
            transparent 68%
        ),
        radial-gradient(
            ellipse 110% 72% at 50% -4%,
            rgba(255, 255, 255, 0.22) 0%,
            rgba(255, 255, 255, 0.06) 28%,
            transparent 54%
        );
}

.kbet-widget-energy.kbet-widget-energy--group::after {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, transparent 36%);
}

.kbet-widget-energy.kbet-widget-energy--group.kbet-widget-energy--flat-status {
    background:
        radial-gradient(
            ellipse 125% 82% at 50% -10%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.52) 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.22) 18%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.07) 38%,
            transparent 56%
        ),
        linear-gradient(
            145deg,
            rgba(255, 255, 255, 0.96) 0%,
            rgba(249, 252, 255, 0.92) 50%,
            rgba(239, 246, 255, 0.94) 100%
        ),
        #f7f9fc;
}

.kbet-widget-energy.kbet-widget-energy--group.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off) {
    background:
        radial-gradient(
            ellipse 125% 82% at 50% -10%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.78) 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.32) 18%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.08) 38%,
            transparent 56%
        ),
        linear-gradient(150deg, #27749f 0%, #185b8a 46%, #063b63 100%);
    box-shadow:
        0 0 0 1px rgba(14, 116, 169, 0.18),
        0 14px 30px rgba(14, 116, 169, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.16),
        inset 0 1px 0 rgba(255, 255, 255, 0.32),
        inset 0 -26px 54px rgba(3, 78, 126, 0.2);
}

.kbet-widget-energy.kbet-widget-energy--group.kbet-widget-energy--control-only:not(
        .kbet-widget-energy--power-off
    )::before {
    background:
        radial-gradient(
            ellipse 130% 100% at 50% 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.52) 0%,
            rgba(var(--surface-r, 59), var(--surface-g, 130), var(--surface-b, 246), 0.22) 34%,
            transparent 68%
        ),
        radial-gradient(
            ellipse 110% 72% at 50% -4%,
            rgba(255, 255, 255, 0.22) 0%,
            rgba(255, 255, 255, 0.06) 28%,
            transparent 54%
        );
    opacity: 0.58;
}

.kbet-widget-energy.kbet-widget-energy--group.kbet-widget-energy--control-only:not(
        .kbet-widget-energy--power-off
    )::after {
    background:
        linear-gradient(120deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 36%, transparent 68%),
        radial-gradient(circle at 8% 10%, rgba(255, 255, 255, 0.03), transparent 32%);
    opacity: 0.34;
}

/* 전원 OFF: 테마색 그라데이션 제거 — 무채색 그레이 면 */
.kbet-widget-energy.kbet-widget-energy--power-off {
    background:
        linear-gradient(
            145deg,
            rgba(248, 248, 248, 0.98) 0%,
            rgba(240, 240, 240, 0.98) 48%,
            rgba(234, 234, 234, 0.98) 100%
        ),
        #ececec;
    box-shadow:
        0 0 0 1px rgba(0, 0, 0, 0.07),
        0 8px 18px rgba(0, 0, 0, 0.05),
        0 1px 3px rgba(0, 0, 0, 0.04),
        inset 0 0 0 1px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.75),
        inset 0 -1px 0 rgba(0, 0, 0, 0.07);
}

.kbet-widget-energy.kbet-widget-energy--power-off::before {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45) 0%, transparent 42%);
    opacity: 0.5;
}

.kbet-widget-energy.kbet-widget-energy--power-off::after {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, transparent 46%);
    opacity: 0.28;
}

.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off) {
    --equipment-text-primary: #f8fafc;
    --equipment-text-strong: #f8fafc;
    --equipment-text-muted: #b8d2e0;
    --equipment-text-soft: #92b8cc;
    --equipment-chip-text: #cbe3ee;
    --equipment-control-blue: #96cce6;
    --equipment-control-gray: #aebbd0;
    --equipment-control-neutral: #d8e1ee;
    --equipment-group-accent: #96cce6;
    --equipment-bulb-yellow: #ffd166;

    background:
        radial-gradient(circle at 16% 0%, rgba(255, 244, 186, 0.06) 0%, rgba(255, 244, 186, 0.02) 29%, transparent 52%),
        linear-gradient(150deg, #27749f 0%, #185b8a 46%, #063b63 100%);
    box-shadow:
        0 0 0 1px rgba(14, 116, 169, 0.18),
        0 14px 30px rgba(14, 116, 169, 0.2),
        inset 0 0 0 1px rgba(255, 255, 255, 0.16),
        inset 0 1px 0 rgba(255, 255, 255, 0.32),
        inset 0 -26px 54px rgba(3, 78, 126, 0.2);
}

.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off)::before {
    background:
        radial-gradient(circle at 18% 0%, rgba(255, 247, 202, 0.06) 0%, rgba(255, 247, 202, 0.02) 30%, transparent 58%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%);
    opacity: 0.58;
}

.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off)::after {
    background:
        linear-gradient(120deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 36%, transparent 68%),
        radial-gradient(circle at 8% 10%, rgba(255, 255, 255, 0.03), transparent 32%);
    opacity: 0.34;
}

.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off)
    .equipment-op-rate-inline__label {
    color: #cbd5e1;
}

/* 가동률 %: color는 .equipment-op-rate-inline__value 에서 var(--equipment-text-strong). TV 거리용으로 파란 제어 카드에서만 숫자에 음영 추가 */
.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off)
    .equipment-op-rate-inline__value {
    text-shadow: 0 1px 2px rgb(15 23 42 / 0.42);
}

.kbet-widget-energy.kbet-widget-energy--control-only:not(.kbet-widget-energy--power-off)
    .equipment-op-rate-inline__track {
    background: linear-gradient(
        90deg,
        var(--op-rate-color, var(--equipment-control-blue)) 0 var(--op-rate),
        rgba(148, 163, 184, 0.34) var(--op-rate)
    );
}

.equipment-widget-content {
    position: relative;
    z-index: 2;
    height: 100%;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 12px;
    color: var(--equipment-text-primary);
    transition:
        opacity 0.3s ease,
        filter 0.3s ease;
}

.equipment-widget-content.is-off {
    opacity: 1;
    filter: none;
}

.equipment-widget-content.is-off .control-only-body,
.equipment-widget-content.is-off .metric-panel {
    opacity: 0.52;
    filter: grayscale(0.28) saturate(0.45);
}

.equipment-widget-content--group .metric-panel {
    gap: 8px;
}

.control-only-body {
    position: relative;
    z-index: 2;
    height: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    padding: 4px 0;
    gap: 8px;
}

.control-only-body--flat-status {
    justify-content: flex-start;
    padding-top: 0;
}

.control-only-icon-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
}

.control-only-icon-row--flat-status {
    gap: 8px;
}

.control-only-icon-row--flat-status .control-only-switch-inline {
    margin-left: auto;
}

.control-only-icon-stage {
    position: relative;
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
}

.control-light-rays {
    --control-sun-pos: 96px 18px;

    position: absolute;
    left: -74px;
    top: -128px;
    z-index: 1;
    width: 226px;
    height: 224px;
    pointer-events: none;
    mix-blend-mode: screen;
    opacity: 0.96;
}

.control-light-rays__layer {
    display: block;
    position: absolute;
    inset: 0;
    transform-origin: var(--control-sun-pos);
    animation: control-light-rays-sway 3.5s linear infinite alternate;
    -webkit-mask-image: radial-gradient(circle at var(--control-sun-pos), white 0%, transparent 80%);
    mask-image: radial-gradient(circle at var(--control-sun-pos), white 0%, transparent 80%);
}

.control-light-rays__layer--1 {
    background: conic-gradient(
        from 128deg at var(--control-sun-pos),
        transparent 10.5%,
        rgba(255, 255, 255, 0.26) 11%,
        transparent 11.5%,
        transparent 15%,
        rgba(255, 235, 176, 0.2) 16%,
        transparent 17%,
        transparent 18%,
        rgba(255, 255, 255, 0.18) 19%,
        transparent 20%
    );
}

.control-light-rays__layer--2 {
    animation-delay: -1s;
    animation-duration: 7s;
    background: conic-gradient(
        from 134deg at var(--control-sun-pos),
        transparent 10%,
        rgba(255, 255, 255, 0.18) 11%,
        transparent 12%,
        transparent 15.5%,
        rgba(255, 246, 205, 0.18) 16%,
        transparent 16.5%,
        transparent 17%,
        rgba(125, 211, 252, 0.16) 18%,
        transparent 19%
    );
}

.control-light-rays__layer--3 {
    animation-delay: -2s;
    animation-duration: 6.5s;
    background: conic-gradient(
        from 140deg at var(--control-sun-pos),
        transparent 10%,
        rgba(255, 249, 220, 0.2) 11%,
        transparent 12%,
        transparent 14%,
        rgba(255, 255, 255, 0.16) 15%,
        transparent 16%,
        transparent 17%,
        rgba(255, 255, 255, 0.16) 18%,
        transparent 19%
    );
}

.control-only-switch-inline {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

.control-only-title-row {
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    align-self: stretch;
}

.control-only-title-row,
.control-only-title-lead {
    width: 100%;
    min-width: 0;
}

.control-only-title-lead {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4px;
}

.control-only-title-lead .control-node-name {
    width: 100%;
    max-width: 100%;
    flex: 0 0 auto;
    text-align: left;
    align-self: stretch;
}

.control-only-switch-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    min-height: 28px;
}

.equipment-op-rate-inline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    flex: 0 0 auto;
    min-width: 0;

    cursor: default;
}

.equipment-op-rate-inline--control-only-stack {
    width: 100%;
    justify-content: flex-start;
    gap: 8px;
}

.equipment-op-rate-inline__label {
    flex: 0 0 auto;
    font-size: 10px;
    line-height: 1;
    font-weight: 750;
    color: var(--equipment-control-gray);
}

.equipment-op-rate-inline__value {
    flex: 0 0 auto;
    font-size: 13px;
    line-height: 1;
    font-weight: 900;
    /* 밝은 표면은 진한 슬레이트, 제어 전용 파란 면은 루트에서 밝은색으로 덮임 */
    color: var(--equipment-text-strong);
    font-variant-numeric: tabular-nums;
}

.equipment-op-rate-inline__track {
    flex: 1 1 56px;
    display: block;
    min-width: 48px;
    height: 8px;
    border-radius: var(--equipment-pill-radius);
    background: linear-gradient(
        90deg,
        var(--op-rate-color, var(--equipment-control-blue)) 0 var(--op-rate),
        rgba(203, 213, 225, 0.72) var(--op-rate)
    );
    box-shadow:
        0 0 10px var(--op-rate-shadow-color, rgba(59, 130, 246, 0.24)),
        inset 0 0 0 1px rgba(148, 163, 184, 0.2);
}

.equipment-op-rate-inline--control-only-stack .equipment-op-rate-inline__track {
    min-width: 0;
}

.equipment-op-rate-inline__live-dot,
.group-op-rate-live-dot,
.mode-chip-status-dot {
    position: relative;
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: equipment-status-dot-blink 1.2s ease-in-out infinite;
}

.equipment-op-rate-inline__live-dot {
    background: var(--equipment-live-green);
}

.equipment-op-rate-inline__live-dot::before,
.group-op-rate-live-dot::before,
.mode-chip-status-dot::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--status-dot-halo-size);
    height: var(--status-dot-halo-size);
    border-radius: 50%;
    filter: blur(4px);
    transform: translate(-50%, -50%) scale(0.88);
    animation: equipment-status-dot-halo 1.2s ease-in-out infinite;
    pointer-events: none;
}

.equipment-op-rate-inline__live-dot::before {
    --status-dot-halo-size: 16px;
    --status-dot-halo-rest-opacity: 0.16;
    --status-dot-halo-active-opacity: 0.4;
    background: var(--equipment-live-green);
}

.group-op-rate-live-dot,
.equipment-widget-content--group .equipment-op-rate-inline__live-dot {
    background: var(--equipment-group-accent);
}

.group-op-rate-live-dot::before,
.equipment-widget-content--group .equipment-op-rate-inline__live-dot::before {
    --status-dot-halo-size: 14px;
    --status-dot-halo-rest-opacity: 0.14;
    --status-dot-halo-active-opacity: 0.38;
    background: var(--equipment-group-accent);
}

@keyframes equipment-status-dot-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.35;
    }
}

@keyframes equipment-status-dot-halo {
    0%,
    100% {
        opacity: var(--status-dot-halo-rest-opacity);
        transform: translate(-50%, -50%) scale(0.88);
    }
    50% {
        opacity: var(--status-dot-halo-active-opacity);
        transform: translate(-50%, -50%) scale(1.45);
    }
}

/* 노드/룸 이름: 말줄임·굵기 공통, 색만 역할별 분리 */
.equipment-node-name,
.control-node-name {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0;
    font-size: 14px;
    font-weight: 700;
}

.equipment-node-name {
    color: var(--equipment-text-strong);
}

.control-node-name {
    color: var(--equipment-text-primary);
}

.equipment-node-type-chip {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    max-width: 82px;
    height: 22px;
    padding: 0 8px;
    border: 1px solid rgba(59, 130, 246, 0.14);
    border-radius: var(--equipment-pill-radius);
    background: rgba(239, 246, 255, 0.62);
    color: #2563eb;
    font-size: 10px;
    font-weight: 850;
    line-height: 1;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.62);
}

.equipment-node-type-chip i {
    flex: 0 0 auto;
    font-size: 10px;
}

.equipment-node-type-chip span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.control-only-icon-row .control-equipment-icon {
    flex-shrink: 0;
    min-width: 0;
    position: relative;
    /* .control-light-rays(z-index:1)보다 위에 두어 광선이 아이콘 뒤로만 보이게 */
    z-index: 2;
    text-shadow:
        0 0 18px rgba(255, 255, 255, 0.32),
        0 10px 22px rgba(2, 6, 23, 0.26);
}

.control-equipment-icon {
    align-self: center;
    font-size: 32px;
    transition: color 0.25s ease;
}

.control-equipment-icon--on {
    color: var(--equipment-control-blue);
}

.control-equipment-icon--bulb {
    color: var(--equipment-bulb-yellow);
}

.control-only-icon-row .control-equipment-icon--bulb {
    text-shadow:
        0 0 16px rgba(255, 209, 102, 0.55),
        0 0 28px rgba(255, 209, 102, 0.28),
        0 10px 22px rgba(2, 6, 23, 0.26);
}

.control-equipment-icon--group {
    color: var(--equipment-group-accent);
}

.control-equipment-icon--off {
    color: var(--equipment-control-gray);
}

.control-equipment-icon--neutral {
    color: var(--equipment-control-neutral);
}

.equipment-widget-content.is-off .control-light-rays {
    opacity: 0;
}

.equipment-widget-content.is-off .control-light-rays__layer {
    animation-play-state: paused;
}

.equipment-widget-content.is-off .control-only-body .equipment-op-rate-inline__live-dot {
    animation: none;
    background: var(--equipment-control-gray);
}

.equipment-widget-content.is-off .control-only-body .equipment-op-rate-inline__live-dot::before {
    animation: none;
    opacity: 0.24;
    background: var(--equipment-control-gray);
    transform: translate(-50%, -50%) scale(1);
}

.equipment-widget-content.is-off .group-op-rate-live-dot,
.equipment-widget-content.is-off .group-op-rate-live-dot::before {
    animation: none;
}

.equipment-widget-content.is-off .group-op-rate-live-dot {
    background: var(--equipment-control-gray);
    box-shadow: none;
}

.equipment-widget-content.is-off .group-op-rate-live-dot::before {
    opacity: 0.24;
    background: var(--equipment-control-gray);
    transform: translate(-50%, -50%) scale(1);
}

.full-mode-body,
.metric-panel {
    position: relative;
    z-index: 2;
}

.full-mode-body {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.metric-panel {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    gap: 10px;
    padding: 0;
}

.equipment-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    margin-top: 3px;
}

.equipment-top-row-lead {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1 1 auto;
}

.equipment-top-row-lead .equipment-node-name {
    flex: 1 1 auto;
}

.equipment-top-row-lead .equipment-op-rate-inline {
    margin-left: auto;
}

.equipment-top-row .equipment-switch-wrap {
    flex-shrink: 0;
}

.equipment-group-overview {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 11px;

    cursor: default;
}

.group-op-rate-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}

.group-op-rate-header__label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    color: var(--equipment-text-muted);
    font-size: 10px;
    font-weight: 800;
}

.equipment-node-type-chip--group-op-rate {
    flex: 0 0 auto;
    height: 20px;
    padding: 0 7px;
    font-size: 9px;
}

.group-op-rate-meter-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    min-width: 0;
}

.group-op-rate-pill-meter {
    display: grid;
    width: 100%;
    min-width: 0;
    grid-template-columns: repeat(var(--group-op-rate-slots, 10), minmax(6px, 1fr));
    align-items: end;
    height: 52px;
    gap: 5px;
}

.group-op-rate-pill-meter__slot {
    display: block;
    width: 100%;
    min-width: 6px;
    height: 30px;
    border-radius: var(--equipment-pill-radius);
    background: rgba(226, 232, 240, 0.95);
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.group-op-rate-pill-meter__slot:nth-child(5n + 1) {
    height: 28px;
}

.group-op-rate-pill-meter__slot:nth-child(5n + 2) {
    height: 35px;
}

.group-op-rate-pill-meter__slot:nth-child(5n + 3) {
    height: 40px;
}

.group-op-rate-pill-meter__slot:nth-child(5n + 4) {
    height: 32px;
}

.group-op-rate-pill-meter__slot:nth-child(5n + 5) {
    height: 30px;
}

.group-op-rate-pill-meter__slot--active {
    background: linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%);
    box-shadow:
        0 0 6px rgba(59, 130, 246, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.group-op-rate-meter-row__value {
    align-self: end;
    min-width: 2.4em;
    color: #2563eb;
    font-size: 16px;
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
}

@container (max-width: 220px) {
    .group-op-rate-pill-meter {
        grid-template-columns: repeat(var(--group-op-rate-slots, 10), minmax(3px, 1fr));
        height: 38px;
        gap: 3px;
    }

    .group-op-rate-pill-meter__slot {
        min-width: 3px;
        height: 22px;
    }

    .group-op-rate-pill-meter__slot:nth-child(5n + 1) {
        height: 20px;
    }

    .group-op-rate-pill-meter__slot:nth-child(5n + 2) {
        height: 25px;
    }

    .group-op-rate-pill-meter__slot:nth-child(5n + 3) {
        height: 28px;
    }

    .group-op-rate-pill-meter__slot:nth-child(5n + 4) {
        height: 23px;
    }

    .group-op-rate-pill-meter__slot:nth-child(5n + 5) {
        height: 21px;
    }

    .group-op-rate-meter-row__value {
        font-size: 19px;
    }
}

.group-op-rate-legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px 8px;
    min-width: 0;
}

.group-op-rate-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    color: var(--equipment-text-soft);
    font-size: 9px;
    font-weight: 750;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.group-op-rate-legend__dot {
    flex: 0 0 auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(203, 213, 225, 0.9);
}

.group-op-rate-legend__dot--on {
    background: #3b82f6;
}

.group-op-rate-legend__dot--off {
    background: #cbd5e1;
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.36);
}

.group-op-rate-legend__dot--other {
    background: #94a3b8;
}

.group-op-rate-legend__label {
    min-width: 0;
}

.group-op-rate-legend__value {
    color: var(--equipment-text-strong);
    font-size: 10px;
    font-weight: 900;
}

.hero-row {
    min-height: 0;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
}

.hero-stat {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.hero-stat--primary {
    color: #7b8796;
    opacity: 0.72;
}

.hero-stat--emphasized {
    align-items: flex-end;
    margin-left: auto;
    color: #2563eb;
}

.hero-label {
    color: currentColor;
    font-size: 10px;
    font-weight: 800;
    opacity: 0.76;
}

.hero-reading {
    display: inline-flex;
    align-items: flex-end;
    color: currentColor;
    white-space: nowrap;
}

.hero-value {
    color: currentColor;
    font-weight: 900;
    letter-spacing: 0;
    font-size: 20px;
}

.hero-stat--emphasized .hero-value {
    text-shadow: 0 6px 14px rgba(37, 99, 235, 0.18);
}

.hero-unit {
    margin-left: 2px;
    color: currentColor;
    font-weight: 850;
    opacity: 0.82;
}

/* .hero-stat--emphasized .hero-unit {
    margin-top: 5px;
} */

.meta-section {
    min-width: 0;
    margin-top: auto;
}

.meta-split-line {
    width: 100%;
    height: 1px;
    margin-bottom: 7px;
    background: linear-gradient(90deg, rgba(114, 128, 146, 0.18), rgba(114, 128, 146, 0.34), rgba(114, 128, 146, 0.08));
}

.mode-meta-row {
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 5px 8px;
}

.mode-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 22px;
    max-width: 100%;
    padding: 4px 8px;
    border-radius: var(--equipment-pill-radius);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
}

.mode-chip-status-dot {
    background: currentColor;
}

.mode-chip-status-dot::before {
    --status-dot-halo-size: 10px;
    --status-dot-halo-rest-opacity: 0.18;
    --status-dot-halo-active-opacity: 0.42;
    background: currentColor;
}

.mode-chip--cool {
    background: rgba(20, 120, 190, 0.13);
    color: #126da8;
}

.mode-chip--heat {
    background: rgba(205, 90, 58, 0.14);
    color: #a9472d;
}

.mode-chip--neutral {
    background: rgba(80, 96, 116, 0.12);
    color: #4c5e74;
}

.meta-labels-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: flex-end;
    gap: 0 2px;
    min-width: 0;
    flex: 1;
    font-size: 10px;
}

.meta-labeled-unit {
    display: inline-flex;
    align-items: baseline;
    max-width: 100%;
}

.meta-labeled-pair {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
    min-width: 0;
}

.meta-labeled-label {
    flex-shrink: 0;
    color: var(--equipment-text-soft);
    font-weight: 700;
}

.meta-labeled-value {
    min-width: 0;
    color: #4c5e74;
    font-weight: 700;
    max-width: 5.5em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.meta-labeled-dot {
    flex-shrink: 0;
    padding: 0 3px;
    color: #b0bcc9;
    font-weight: 600;
}

.widget-empty-wrap {
    height: 100%;
    position: relative;
    z-index: 2;
    padding: 10px;
}

.no-node-info-guide {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 7px;
    color: var(--equipment-text-muted);
    text-align: center;
}

.empty-state-icon {
    font-size: 30px;
    margin-bottom: 10px;
    color: var(--equipment-text-muted);
    opacity: 0.38;
}

.empty-state-message {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.45;
}

/* ── 에러 뱃지 ───────────────────────────────────────────── */
.kbet-custom-reset {
    position: relative;
}

.equipment-error-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ef4444;
    color: #fff;
    font-size: 10px;
    box-shadow:
        0 0 0 2.5px rgba(255, 255, 255, 0.72),
        0 2px 8px rgba(239, 68, 68, 0.38);
    animation: equipment-error-pulse 2.2s ease-in-out infinite;
    cursor: default;
    pointer-events: auto;
}

@keyframes equipment-error-pulse {
    0%,
    100% {
        box-shadow:
            0 0 0 2.5px rgba(255, 255, 255, 0.72),
            0 2px 8px rgba(239, 68, 68, 0.38);
    }
    50% {
        box-shadow:
            0 0 0 2.5px rgba(255, 255, 255, 0.72),
            0 0 0 6px rgba(239, 68, 68, 0.14),
            0 2px 12px rgba(239, 68, 68, 0.52);
    }
}

.equipment-error-fade-enter-active,
.equipment-error-fade-leave-active {
    transition:
        opacity 0.28s ease,
        transform 0.28s ease;
}

.equipment-error-fade-enter,
.equipment-error-fade-leave-to {
    opacity: 0;
    transform: scale(0.6);
}

/* ── 툴팁 ────────────────────────────────────────────────── */
::v-deep .tooltip-inner {
    white-space: pre-line;
    text-align: left;
    max-width: 200px;
    min-width: 50px;
    font-size: 12px;
}
</style>
